import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Star, TrendingUp, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ProductCard from '@/components/product/ProductCard';
import ProductCardSkeleton from '@/components/product/ProductCardSkeleton';
import Image from '@/components/ui/Image';
import Button from '@/components/ui/Button';
import { Category } from '@/services/productsService';
import { Product } from '@/services/types/domain';
import productsService from '@/services/productsService';
//import { config } from '@/config';
import { placeholders } from '@/utils/placeholders';
import { toast } from 'react-hot-toast';
import { UPLOADS_URL } from '@/utils/utils';

// Hero section component
const HeroSection: React.FC = () => {
    return (
        <section className="relative bg-gradient-to-r from-primary-600 to-primary-800 text-white overflow-hidden w-full">
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
                            Descubre productos increíbles a{' '}
                            <span className="text-accent-400">precios únicos</span>
                        </h1>
                        <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 text-primary-100">
                            Miles de productos de calidad, envío rápido y la mejor atención al cliente.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <Button
                                size="lg"
                                variant="outline"
                                className="border-white text-white hover:bg-white hover:text-primary-600 w-full sm:w-auto"
                            >
                                Explorar productos
                            </Button>

                            <Button
                                size="lg"
                                variant="outline"
                                className="border-white text-white hover:bg-white hover:text-primary-600 w-full sm:w-auto"
                            >
                                Ver ofertas
                            </Button>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="relative order-first lg:order-last"
                    >
                        <div className="relative w-full rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden">
                            {/* Removido aspect-video y ajustado el contenedor */}
                            <div className="relative w-full max-w-sm sm:max-w-md lg:max-w-lg mx-auto lg:max-w-none">
                                <Image
                                    src={UPLOADS_URL + "caratula_eisegmi.png"}
                                    alt="Shopping"
                                    fallback={placeholders.general}
                                    className="w-full h-auto object-contain rounded-xl sm:rounded-2xl"
                                    containerClassName="w-full"
                                />
                            </div>

                            {/* Floating elements */}
                            <div className="hidden sm:block absolute top-4 right-4 z-10 bg-accent-400 text-primary-900 px-3 py-1 rounded-full font-bold text-sm animate-bounce shadow-md">
                                ¡Nuevos productos!
                            </div>
                            <div className="hidden sm:block absolute bottom-4 left-4 z-10 bg-white text-primary-600 px-3 py-1 rounded-full font-bold text-sm shadow-md">
                                Envío gratis
                            </div>
                        </div>

                    </motion.div>
                </div>
            </div>
        </section>
    );
};

// Categories section component
const CategoriesSection: React.FC<{ categories: Category[] }> = ({ categories }) => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    return (
        <section ref={ref} className="py-8 sm:py-12 lg:py-16 bg-white w-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-8 sm:mb-12">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.6 }}
                        className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-4"
                    >
                        Explora por categorías
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-gray-600 text-sm sm:text-base"
                    >
                        Encuentra exactamente lo que buscas
                    </motion.p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 sm:gap-4">
                    {categories.slice(0, 8).map((category, index) => (
                        <motion.div
                            key={category.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                        >
                            <Link
                                to={`/category/${category.id}`}
                                className="group block text-center p-2 sm:p-4 rounded-lg hover:shadow-md transition-all duration-300"
                            >
                                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-100 rounded-full mx-auto mb-2 sm:mb-3 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                                    <Image
                                        src={UPLOADS_URL + category.image}
                                        alt={category.name}
                                        fallback={placeholders.category}
                                        className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                                        showPlaceholder={false}
                                    />
                                </div>
                                <h3 className="font-medium text-gray-900 group-hover:text-primary-600 transition-colors text-xs sm:text-sm leading-tight">
                                    {category.name}
                                </h3>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// Featured products section
const FeaturedProductsSection: React.FC<{ products: Product[]; isLoading: boolean }> = ({
    products,
    isLoading
}) => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    return (
        <section ref={ref} className="py-8 sm:py-12 lg:py-16 bg-gray-50 w-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 sm:mb-12">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.6 }}
                        className="mb-4 sm:mb-0"
                    >
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 flex items-center">
                            <Star className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 mr-2 sm:mr-3" />
                            Productos destacados
                        </h2>
                        <p className="text-gray-600 text-sm sm:text-base">Los productos más populares de nuestra tienda</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.6 }}
                    >
                        <Link
                            to="/products?featured=true"
                            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base"
                        >
                            Ver todos
                            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1" />
                        </Link>
                    </motion.div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                    {isLoading ? (
                        // Mostrar skeletons mientras carga
                        [...Array(8)].map((_, index) => (
                            <motion.div
                                key={`skeleton-${index}`}
                                initial={{ opacity: 0, y: 20 }}
                                animate={inView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                            >
                                <ProductCardSkeleton />
                            </motion.div>
                        ))
                    ) : products.length > 0 ? (
                        // Mostrar productos reales
                        products.map((product, index) => (
                            <motion.div
                                key={product.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={inView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                            >
                                <ProductCard
                                    product={product}
                                    showQuickAdd
                                    showWishlist
                                    imageSize="md"
                                />
                            </motion.div>
                        ))
                    ) : (
                        // Mensaje cuando no hay productos
                        <div className="col-span-full text-center py-8 sm:py-12">
                            <div className="text-gray-400 mb-4">
                                <Star className="w-12 h-12 sm:w-16 sm:h-16 mx-auto" />
                            </div>
                            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                                No hay productos destacados
                            </h3>
                            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                                Vuelve más tarde para ver nuestros productos destacados
                            </p>
                            <Button variant="outline" size="sm">
                                <Link to="/products">Ver todos los productos</Link>
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

//Stats section
const StatsSection: React.FC = () => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const stats = [
        { icon: <TrendingUp className="w-8 h-8" />, value: '50K+', label: 'Productos' },
        { icon: <Star className="w-8 h-8" />, value: '4.8', label: 'Calificación promedio' },
        { icon: <Zap className="w-8 h-8" />, value: '24h', label: 'Envío rápido' },
        { icon: <TrendingUp className="w-8 h-8" />, value: '99%', label: 'Clientes satisfechos' },
    ];

    return (
        <section ref={ref} className="py-16 bg-primary-600 text-white w-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                    {stats.map((stat, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            className="text-center"
                        >
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500 rounded-full mb-4">
                                {stat.icon}
                            </div>
                            <div className="text-3xl font-bold mb-2">{stat.value}</div>
                            <div className="text-primary-200">{stat.label}</div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// Main HomePage component
const HomePage: React.FC = () => {
    const [featuredProducts, setFeaturedProducts] = useState<any[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);

                // Cargar datos en paralelo
                const [productsResponse, categoriesResponse] = await Promise.all([
                    productsService.getFeaturedProducts(8),
                    productsService.getCategories(),
                ]);

                //console.log('Featured products response:', productsResponse);
                //console.log('Categories response:', categoriesResponse);

                setFeaturedProducts(productsResponse);
                setCategories(categoriesResponse);
            } catch (error) {
                console.error('Error loading homepage data:', error);
                toast.error('Error al cargar los datos de la página');
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, []);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center w-full">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="w-full">
            <HeroSection />
            <CategoriesSection categories={categories} />
            <FeaturedProductsSection products={featuredProducts} isLoading={isLoading} />
            <StatsSection />
        </div>
    );
};

export default HomePage;